import {
  MEDIA_FILE_REJECTION_CODES,
  MEDIA_HASH_CHECK_MAX,
  type MediaHashMatch,
  type MediaUploadResponse,
} from "@ckgo2/shared";
import type { MediaBulkAction } from "@ckgo2/shared/types";
import { acceptBatchFiles } from "./batchFiles";
import type {
  BatchFile,
  BatchFileStatus,
  BatchOptions,
  BatchPermanentReason,
  BatchSummary,
  BatchUpload,
} from "./batchUpload.types";
import { uploadTypeOf } from "./fileTypes";

/**
 * Řadič dávky nahrávání (vize podnety-tymu-2026-10, F7.8–F7.10, řez 04): otisky souborů,
 * kontrola duplicit bez odeslání bajtů, souběžné nahrávání, opakování, obnova z koše a převzetí
 * země a zdroje existujícími fotkami. Bez Reactu, `api` klienta i `window`: všechno vnější
 * dostane v `BatchDeps`, takže jde testovat v projektu `admin` (prostředí `node`). Hák
 * `useBatchUpload` ho jen napojí (`useSyncExternalStore`). Sémantika rozhraní:
 * `batchUpload.types.ts`.
 */

export interface BatchDeps {
  hashFile(file: File): Promise<string>;
  checkHashes(hashes: string[]): Promise<MediaHashMatch[]>;
  /** Trvalá chyba známá bez rozměrů (TIF bez dekodéru, HEIC bez dekodéru nad prahem bajtů), jinak null. */
  precheck(file: File): Promise<BatchPermanentReason | null>;
  /**
   * Příprava v prohlížeči (`prepareForUpload`, dekódování serializuje sama). `signal` se zruší
   * s dávkou (`reset`, nová dávka): příprava, která na dekódování teprve čeká, nedekóduje.
   */
  prepare(
    file: File,
    signal: AbortSignal,
  ): Promise<{ blob: Blob; resized: boolean } | { permanent: BatchPermanentReason }>;
  upload(form: FormData): Promise<MediaUploadResponse>;
  adopt(body: AdoptBody): Promise<unknown>;
  restore(mediaId: string): Promise<unknown>;
  now(): number;
  /** Prodleva před automatickým opakováním přechodně selhaného nahrání (`UPLOAD_RETRY_DELAYS_MS`). */
  wait(ms: number): Promise<void>;
  concurrency: number;
}

/** Tělo `POST /api/admin/upload/bulk` převzetí: větev sdíleného `MediaBulkActionSchema`. */
type AdoptBody = Extract<MediaBulkAction, { action: "adoptFromBatch" }>;

export type BatchSnapshot = Pick<
  BatchUpload,
  "files" | "rejected" | "running" | "startedAt" | "finishedAt" | "summary"
>;

export interface BatchController extends Omit<BatchUpload, keyof BatchSnapshot> {
  /** Stejná identita, dokud se stav nezmění. */
  getSnapshot(): BatchSnapshot;
  subscribe(listener: () => void): () => void;
}

/** Volba řezu (krátké požadavky, opakování po dávkách), ne strop schématu `bulkIds`. */
export const ADOPT_IDS_PER_REQUEST = 50;

/* ---------- čisté funkce ---------- */

/** `done`; `existing` = tři druhy duplicit; `errors` = obě chyby; ostatní stavy nikam. */
export function summarizeBatch(files: readonly BatchFile[]): BatchSummary {
  const summary: BatchSummary = { done: 0, existing: 0, errors: 0 };
  for (const { status } of files) {
    if (status === "done") summary.done += 1;
    else if (
      status === "duplicate-live" ||
      status === "duplicate-trash" ||
      status === "duplicate-in-batch"
    )
      summary.existing += 1;
    else if (status === "error" || status === "error-permanent") summary.errors += 1;
  }
  return summary;
}

/** Chybová pole souboru; do `BatchFile` se přenesou jedním přiřazením. */
type Failure = Pick<BatchFile, "error" | "errorCode" | "errorParams" | "permanentReason">;
type ErrorStatus = Extract<BatchFileStatus, "error" | "error-permanent">;

const REJECTION_CODES: ReadonlySet<string> = new Set(MEDIA_FILE_REJECTION_CODES);
/** Odmítnutí podle limitů (bajty, rozměry), ne podle obsahu souboru. */
const LIMIT_CODES: ReadonlySet<string> = new Set(["media_too_large", "media_exceeds_image_limits"]);

/**
 * Co jde z neznámé chyby přečíst. Jediné místo s duck-typingem: model `ApiError` importovat
 * nesmí (bez `api` klienta), takže čte jen tvar (`status`, `code`, `params`, `message`).
 * Text (`error`) se přenese jen z chyby ve tvaru `ApiError` (číselný `status`): ten skládá
 * klient ze slovníku. Chyba prohlížeče (čtení souboru, detekce schopností) nese anglický
 * technický text, takže `error` zůstane prázdné a panel ukáže obecnou hlášku ze slovníku.
 */
function readFailure(err: unknown): {
  status?: number;
  code?: string;
  failure: Failure;
} {
  const e = (typeof err === "object" && err !== null ? err : {}) as {
    status?: unknown;
    code?: unknown;
    params?: unknown;
    message?: unknown;
  };
  const status = typeof e.status === "number" ? e.status : undefined;
  const failure: Failure = {};
  if (status !== undefined && typeof e.message === "string" && e.message !== "") {
    failure.error = e.message;
  }
  const code = typeof e.code === "string" ? e.code : undefined;
  if (code !== undefined) failure.errorCode = code;
  if (typeof e.params === "object" && e.params !== null) {
    failure.errorParams = e.params as Record<string, string | number>;
  }
  return { status, code, failure };
}

/** Chyba otisku, kontroly nebo převzetí: vždy opakovatelná (soubor neodešel nebo odešel celý). */
const retryableFailure = (err: unknown): Failure => readFailure(err).failure;

/**
 * Třídění chyby nahrání (AK33). Trvalá je jen odpověď, kterou opakování nezmění: 400 s kódem
 * odmítnutí souboru (`MEDIA_FILE_REJECTION_CODES`, jeden zdroj se serverem) a 413 z limitu
 * těla zóny (bez JSON, `code: "unknown"`, proto podle `status`). Všechno ostatní je
 * opakovatelné, včetně 500 `media_upload_failed` (poškozený soubor, AK21) a výpadku sítě
 * (`status` 0 z `uploadFormData`; chyba bez `status` taky).
 */
export function classifyUploadFailure(err: unknown): { status: ErrorStatus } & Failure {
  const { status, code, failure } = readFailure(err);
  let permanentReason: BatchPermanentReason | undefined;
  if (status === 400 && code !== undefined && REJECTION_CODES.has(code)) {
    permanentReason = LIMIT_CODES.has(code) ? "server-limits" : "server-rejected";
  } else if (status === 413) {
    permanentReason = "server-limits";
  }
  return permanentReason === undefined
    ? { status: "error", ...failure }
    : { status: "error-permanent", ...failure, permanentReason };
}

/**
 * Prodlevy automatických opakování nahrání po přechodné chybě (`isTransientUploadFailure`):
 * kolik prvků, tolik dalších pokusů. Místo souběhu se během prodlevy drží, takže přetížená
 * služba dostane méně požadavků naráz. Po vyčerpání zůstane chyba opakovatelná ručně.
 */
export const UPLOAD_RETRY_DELAYS_MS: readonly number[] = [2000, 6000];

/**
 * Prodlevy ověření otisku po nahrání s neznámým výsledkem (`isUnknownUploadOutcome`): první
 * hned, druhé opožděné, protože server záznam zakládá až po zpracování těla (Images, R2, AI) a
 * spojení se mohlo přerušit dřív, než zápis doběhl (diagnóza E2E kola 3, „Riziko časování“).
 * Místo souběhu se během prodlevy drží jako u opakování nahrání.
 */
export const UPLOAD_VERIFY_DELAYS_MS: readonly number[] = [0, 20_000];

/**
 * Text chyby Workeru z pipeline (`media_upload_failed`, parametr `reason`), který znamená
 * přetížení nebo přerušené spojení, ne vadu souboru: Images 9529 („too busy“) a ztracené
 * spojení podpožadavku. 9516 (poškozený soubor) sem nepatří: opakování ho nezmění (AK21).
 */
const TRANSIENT_UPLOAD_REASON = /\b9529\b|too busy|network connection lost/i;

/**
 * Přechodná chyba nahrání, kterou model zkusí znovu sám (E2E kolo 1, AK1): výpadek sítě bez
 * odpovědi (`status` 0 z `uploadFormData`), 502/503/504 z okraje a 500 `media_upload_failed`
 * s přetížením nebo ztraceným spojením v `reason`. Ostatní opakovatelné chyby čekají na
 * „Zkusit znovu“, aby jedno kliknutí poslalo jeden požadavek.
 */
export function isTransientUploadFailure(err: unknown): boolean {
  const { status, code, failure } = readFailure(err);
  if (status === 0 || status === 502 || status === 503 || status === 504) return true;
  const reason = failure.errorParams?.reason;
  return (
    status === 500 &&
    code === "media_upload_failed" &&
    typeof reason === "string" &&
    TRANSIENT_UPLOAD_REASON.test(reason)
  );
}

/**
 * Chyba nahrání s neznámým výsledkem: odpověď HTTP nedorazila (`status` 0 z `uploadFormData`,
 * syrová chyba bez `status`) nebo ji okraj nahradil 502/504 (Worker mohl doběhnout). Tělo mohlo
 * dojít celé a fotka vzniknout, takže model po vyčerpání pokusů ověří otisk v knihovně, než
 * soubor ohlásí jako nenahraný (E2E kolo 3, AK1 běh 1). 503 a odpovědi Workeru (500
 * `media_upload_failed`) výsledek znají: fotka nevznikla.
 */
export function isUnknownUploadOutcome(err: unknown): boolean {
  const { status } = readFailure(err);
  return status === undefined || status === 0 || status === 502 || status === 504;
}

/**
 * Tělo `POST /api/admin/upload`. Jméno části zůstává původní (hledání `q`, `altFromFilename`);
 * typ normalizuje `uploadTypeOf` (prázdný typ nezmenšeného HEIC/TIF podle přípony, sekvence HEIC
 * → `image/heic`, `validateImage` je nezná). Pole `alt`, `title`, `tags` ani `description` se
 * neposílají vůbec: přítomné prázdné `description` server bere jako „redakce zvolila prázdné“
 * a AI texty by nevznikly.
 */
export function buildUploadForm(
  file: File,
  prepared: { blob: Blob; resized: boolean },
  hash: string,
  opts: BatchOptions,
): FormData {
  const form = new FormData();
  const type = uploadTypeOf({ type: prepared.blob.type, name: file.name });
  form.append("file", new File([prepared.blob], file.name, { type }));
  form.append("folder", opts.folder);
  if (opts.countryIds.length > 0) form.append("countryIds", JSON.stringify(opts.countryIds));
  if (opts.source !== null) form.append("source", opts.source);
  if (prepared.resized) form.append("originalHash", hash);
  return form;
}

/* ---------- stav dávky ---------- */

type Phase = "check" | "upload" | "adopt";
type Session = "idle" | "running" | "finished";

/** Stav skupiny: soubor ho má jen jako vlastník; `hashing` a `duplicate-in-batch` jsou stavy souboru. */
type GroupStatus = Exclude<BatchFileStatus, "hashing" | "duplicate-in-batch">;

/**
 * Skupina souborů se stejným otiskem: jednotka práce (kontrola, nahrání, převzetí). Soubory
 * téhož otisku odejdou jednou; vlastník (`members[0]`) ukazuje stav skupiny, ostatní ho zrcadlí
 * (AK17). Odebráním vlastníka se vlastníkem stane další člen, stav zůstává ve skupině.
 */
interface Group {
  hash: string;
  /** Klíče členů v pořadí, v jakém dostali otisk. */
  members: string[];
  status: GroupStatus;
  /** Jen ve stavech `error` a `error-permanent`. */
  failure?: Failure;
  /** Fáze poslední opakovatelné chyby: kam vrátí „Zkusit znovu“. */
  phase?: Phase;
  mediaId?: string;
  inGallery?: boolean;
  resized?: boolean;
  /** Otisk už letí v `check-hashes`. */
  checkSent: boolean;
}

/** Soubor dávky. Do otisku má vlastní stav, po otisku (`hash`) ho určuje jeho skupina. */
interface Rec {
  key: string;
  file: File;
  /** Stav před otiskem: čeká na `precheck` a otisk, otisk selhal, nebo trvalá chyba z `precheck`. */
  status: Extract<BatchFileStatus, "hashing" | "error" | "error-permanent">;
  failure?: Failure;
  /** `precheck` doběhl bez trvalé chyby: fronta otisků smí soubor vzít. */
  prechecked: boolean;
  hash?: string;
}

/**
 * Všechno, co patří jedné dávce; `reset()` a `add()` po dokončení dosadí nový objekt. Výsledek
 * asynchronního kroku se uplatní jen na dávku, ve které krok začal (`track`).
 */
interface BatchState {
  order: string[];
  recs: Map<string, Rec>;
  groups: Map<string, Group>;
  rejected: number;
  session: Session;
  startedAt: number | null;
  finishedAt: number | null;
  opts: BatchOptions | null;
  /** `mediaId` existujících fotek, které už šly do fronty převzetí (jednou za fotku v relaci). */
  adopted: Set<string>;
  adoptQueue: string[];
  /** Převzetí této dávky právě letí (konec relace na něj čeká). */
  adoptSending: boolean;
  /** Zruší se s dávkou: příprava, která na dekódování teprve čeká, nedekóduje. */
  abort: AbortController;
  /** Poslední pohled na každý soubor: nezměněný soubor drží identitu napříč snímky. */
  views: Map<string, BatchFile>;
}

function createBatchState(): BatchState {
  return {
    order: [],
    recs: new Map(),
    groups: new Map(),
    rejected: 0,
    session: "idle",
    startedAt: null,
    finishedAt: null,
    opts: null,
    adopted: new Set(),
    adoptQueue: [],
    adoptSending: false,
    abort: new AbortController(),
    views: new Map(),
  };
}

/** Stavy skupiny, ve kterých se ještě pracuje; dokud nějaká je, relace neskončí. */
const ACTIVE_GROUP: ReadonlySet<GroupStatus> = new Set([
  "checking",
  "queued",
  "preparing",
  "uploading",
]);

function sameFile(a: BatchFile, b: BatchFile): boolean {
  const keys = Object.keys(a) as (keyof BatchFile)[];
  return keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k]);
}

function view(b: BatchState, rec: Rec): BatchFile {
  const out: BatchFile = {
    key: rec.key,
    file: rec.file,
    name: rec.file.name,
    size: rec.file.size,
    status: rec.status,
  };
  const g = rec.hash === undefined ? undefined : b.groups.get(rec.hash);
  if (rec.hash === undefined || g === undefined) {
    if (rec.failure) Object.assign(out, rec.failure);
    return out;
  }
  out.hash = rec.hash;
  // Člen zrcadlí vlastníka (AK17): duplicity a chyby přebírá, práce i nová fotka vlastníka
  // je pro něj kopie v dávce.
  const mirrored =
    g.status === "duplicate-live" ||
    g.status === "duplicate-trash" ||
    g.status === "error" ||
    g.status === "error-permanent";
  out.status = g.members[0] === rec.key || mirrored ? g.status : "duplicate-in-batch";
  if (g.failure) Object.assign(out, g.failure);
  if (g.resized !== undefined) out.resized = g.resized;
  if (g.mediaId !== undefined) out.mediaId = g.mediaId;
  if (g.inGallery !== undefined) out.inGallery = g.inGallery;
  return out;
}

type Settled<T> = { ok: true; value: T } | { ok: false; error: unknown };

/**
 * Prostředky souběhu (otisk, kontrola, převzetí, místa nahrání). Letící požadavek dávky se
 * resetem nezruší (`uploadFormData` neumí `AbortSignal`), takže místo drží, dokud neodpoví,
 * i když hostitel mezitím odpojil řadič a připojil nový. Strop proto platí pro všechny
 * řadiče, které sadu sdílejí (hák: jedna sada na kartu prohlížeče).
 */
export interface BatchResources {
  hashBusy: boolean;
  checkBusy: boolean;
  adoptBusy: boolean;
  uploadSlots: number;
  /** Řadiče s odběratelem: uvolněný prostředek je probudí, aby si ho vzaly. */
  waiters: Set<() => void>;
}

export function createBatchResources(): BatchResources {
  return {
    hashBusy: false,
    checkBusy: false,
    adoptBusy: false,
    uploadSlots: 0,
    waiters: new Set(),
  };
}

/* ---------- řadič ---------- */

export function createBatchController(
  deps: BatchDeps,
  pool: BatchResources = createBatchResources(),
): BatchController {
  let batch = createBatchState();
  let nextKey = 0;

  const listeners = new Set<() => void>();
  let snapshot: BatchSnapshot = {
    files: [],
    rejected: 0,
    running: false,
    startedAt: null,
    finishedAt: null,
    summary: null,
  };

  /** Skupina pořád patří aktuální dávce (nikdo ji neodebral). */
  const alive = (g: Group) => batch.groups.get(g.hash) === g;

  /**
   * Asynchronní krok dávky: synchronní výjimku z `work` převede na odmítnutí, výsledek předá
   * `apply` jen pro dávku, ve které krok začal (po `reset` se zahodí), pak uvolní prostředek
   * (a probudí řadiče, které na něj čekají) a naplánuje překreslení. Vrácený Promise se splní
   * po tomto všem, takže kdo na něj čeká, uvidí už nový snímek.
   */
  function track<T>(
    work: () => Promise<T>,
    apply: (result: Settled<T>) => void,
    release?: () => void,
  ): Promise<void> {
    const owner = batch;
    return (async () => work())()
      .then(
        (value): Settled<T> => ({ ok: true, value }),
        (error: unknown): Settled<T> => ({ ok: false, error }),
      )
      .then((result) => {
        if (owner === batch) apply(result);
      })
      .finally(() => {
        if (release) {
          release();
          for (const wake of [...pool.waiters]) wake();
        }
        schedule();
      });
  }

  /** Nový snímek jen při změně: nezměněné soubory i celý snímek drží identitu. `true` = změna. */
  function commit(): boolean {
    const b = batch;
    const prev = snapshot;
    // Pohled se přepočítá všem souborům (`view` je čistá funkce stavu), takže žádný přechod
    // nemusí hlásit, které soubory změnil; identitu drží porovnání se starým pohledem.
    const next = b.order.map((key) => {
      const fresh = view(b, b.recs.get(key) as Rec);
      const old = b.views.get(key);
      if (old !== undefined && sameFile(old, fresh)) return old;
      b.views.set(key, fresh);
      return fresh;
    });
    const files =
      next.length !== prev.files.length || next.some((f, i) => f !== prev.files[i])
        ? next
        : prev.files;
    const running = b.session === "running";
    const finished = b.session === "finished";
    if (
      files === prev.files &&
      prev.rejected === b.rejected &&
      prev.running === running &&
      prev.startedAt === b.startedAt &&
      prev.finishedAt === b.finishedAt &&
      (prev.summary !== null) === finished
    ) {
      return false;
    }
    snapshot = {
      files,
      rejected: b.rejected,
      running,
      startedAt: b.startedAt,
      finishedAt: b.finishedAt,
      summary: finished ? summarizeBatch(files) : null,
    };
    return true;
  }

  function hasActiveWork(b: BatchState): boolean {
    for (const rec of b.recs.values()) {
      if (rec.hash === undefined && rec.status === "hashing") return true;
    }
    for (const g of b.groups.values()) if (ACTIVE_GROUP.has(g.status)) return true;
    return false;
  }

  /** Po každé změně: rozjet fronty, zkontrolovat konec relace, zapsat snímek, ohlásit. */
  function settle() {
    pumpHash();
    pumpCheck();
    pumpUpload();
    pumpAdopt();
    const b = batch;
    if (
      b.session === "running" &&
      b.adoptQueue.length === 0 &&
      !b.adoptSending &&
      !hasActiveWork(b)
    ) {
      b.session = "finished";
      b.finishedAt = deps.now();
    }
    if (commit()) for (const listener of [...listeners]) listener();
  }

  /** Výsledky, které doběhnou v jedné vlně mikroúloh (např. `precheck` celé dávky), překreslí jednou. */
  let settleQueued = false;
  function schedule() {
    if (settleQueued) return;
    settleQueued = true;
    queueMicrotask(() => {
      settleQueued = false;
      settle();
    });
  }

  /** Volby převzetí, když ho tato dávka potřebuje (stránka Média se zemí nebo zdrojem), jinak null. */
  function adoptOpts(b: BatchState): BatchOptions | null {
    const opts = b.opts;
    return opts !== null &&
      opts.adoptExisting &&
      (opts.countryIds.length > 0 || opts.source !== null)
      ? opts
      : null;
  }

  function reopen() {
    if (batch.session === "finished") {
      batch.session = "running";
      batch.finishedAt = null;
    }
  }

  function fail(g: Group, phase: Phase, failure: Failure, status: ErrorStatus = "error") {
    g.status = status;
    g.failure = failure;
    if (status === "error") g.phase = phase;
  }

  /**
   * Fotka, která v knihovně už je (shoda při kontrole, `reused` u nahrání, obnova z koše), po `start`:
   * `inGallery` a při `adoptExisting` jednou za `mediaId` fronta převzetí.
   */
  function existingFound(g: Group) {
    const b = batch;
    if (b.session === "idle" || g.mediaId === undefined) return;
    g.inGallery = b.opts?.selectedIds?.includes(g.mediaId) ?? false;
    if (adoptOpts(b) !== null && !b.adopted.has(g.mediaId)) {
      b.adopted.add(g.mediaId);
      b.adoptQueue.push(g.mediaId);
    }
  }

  function applyMatch(g: Group, match: MediaHashMatch) {
    if (match.live) {
      g.status = "duplicate-live";
      g.mediaId = match.live.id;
      existingFound(g);
    } else if (match.trashed) {
      g.status = "duplicate-trash";
      g.mediaId = match.trashed.id;
    } else {
      g.status = "queued";
    }
  }

  /** Soubor dostal otisk: založí skupinu, nebo se přidá k té, která otisk už má. */
  function joinGroup(rec: Rec, hash: string) {
    rec.hash = hash;
    const g = batch.groups.get(hash);
    if (g) g.members.push(rec.key);
    else batch.groups.set(hash, { hash, members: [rec.key], status: "checking", checkSent: false });
  }

  /** 1. Otisk: nejvýš jeden naráz (čte celý soubor do paměti), jen po `precheck`. */
  function pumpHash() {
    if (pool.hashBusy) return;
    const b = batch;
    let rec: Rec | undefined;
    for (const key of b.order) {
      const r = b.recs.get(key) as Rec;
      if (r.hash === undefined && r.status === "hashing" && r.prechecked) {
        rec = r;
        break;
      }
    }
    if (!rec) return;
    pool.hashBusy = true;
    const { key, file } = rec;
    track(
      () => deps.hashFile(file),
      (res) => {
        const r = b.recs.get(key);
        if (!r || r.status !== "hashing" || r.hash !== undefined) return;
        if (res.ok) {
          joinGroup(r, res.value);
        } else {
          r.status = "error";
          r.failure = retryableFailure(res.error);
        }
      },
      () => {
        pool.hashBusy = false;
      },
    );
  }

  /** 2. Kontrola otisků: jeden požadavek naráz, po nejvýš `MEDIA_HASH_CHECK_MAX`, páruje podle `hash`. */
  function pumpCheck() {
    if (pool.checkBusy) return;
    const sent: Group[] = [];
    for (const g of batch.groups.values()) {
      if (g.status === "checking" && !g.checkSent) sent.push(g);
      if (sent.length === MEDIA_HASH_CHECK_MAX) break;
    }
    if (sent.length === 0) return;
    pool.checkBusy = true;
    for (const g of sent) g.checkSent = true;
    const hashes = sent.map((g) => g.hash);
    track(
      () => deps.checkHashes(hashes),
      (res) => {
        const waiting = sent.filter((g) => alive(g) && g.status === "checking" && g.checkSent);
        for (const g of waiting) g.checkSent = false;
        if (!res.ok) {
          const failure = retryableFailure(res.error);
          for (const g of waiting) fail(g, "check", failure);
          return;
        }
        const byHash = new Map(res.value.map((m) => [m.hash, m]));
        for (const g of waiting) {
          const match = byHash.get(g.hash);
          // Odpověď bez položky k otisku: opakovatelná chyba kontroly bez technického textu
          // (panel ukáže obecnou hlášku ze slovníku).
          if (match) applyMatch(g, match);
          else fail(g, "check", {});
        }
      },
      () => {
        pool.checkBusy = false;
      },
    );
  }

  /**
   * 3. Nahrání: jen v relaci `running`, nejvýš `deps.concurrency` skupin v `preparing` +
   * `uploading`. Příprava se volá souběžně (Kontrakt bod 5: dekódování serializuje sama), takže
   * soubor beze změny nečeká za zmenšením jiného.
   */
  function pumpUpload() {
    const b = batch;
    const opts = b.opts;
    if (b.session !== "running" || opts === null) return;
    for (const g of b.groups.values()) {
      if (pool.uploadSlots >= deps.concurrency) return;
      if (g.status !== "queued") continue;
      g.status = "preparing";
      pool.uploadSlots += 1;
      runUpload(b, g, opts);
    }
  }

  function runUpload(b: BatchState, g: Group, opts: BatchOptions) {
    const file = (b.recs.get(g.members[0]) as Rec).file;
    let uploading = false;
    track(
      () => deps.prepare(file, b.abort.signal),
      (res) => {
        if (!alive(g) || g.status !== "preparing") return;
        // `prepareForUpload` podle Kontraktu nevyhazuje; kdyby přesto, opakování dává smysl.
        if (!res.ok) {
          fail(g, "upload", retryableFailure(res.error));
          return;
        }
        const prepared = res.value;
        if ("permanent" in prepared) {
          fail(g, "upload", { permanentReason: prepared.permanent }, "error-permanent");
          return;
        }
        g.status = "uploading";
        g.resized = prepared.resized;
        uploading = true;
        track(
          () => uploadOrFind(b, g, buildUploadForm(file, prepared, g.hash, opts)),
          (up) => {
            if (!alive(g) || g.status !== "uploading") return;
            if (!up.ok) {
              const { status, ...failure } = classifyUploadFailure(up.error);
              fail(g, "upload", failure, status);
              return;
            }
            g.mediaId = up.value.id;
            // Odchází jen skupina, kterou kontrola otisků v knihovně nenašla, takže `reused`
            // tady neznamená „byla tu dřív“: fotka vznikla během této dávky (síť nebo okraj
            // doručil tělo podruhé, předchozí pokus ji založil a odpověď se ztratila, nebo ji
            // mezitím nahrál někdo jiný; totéž u fotky, kterou našlo ověření otisku po ztracené
            // odpovědi v `uploadOrFind`). Hotovo, ne „Už v knihovně“ (E2E kolo 1, AK1 běh 3);
            // převzetí země a zdroje platí jako u existující fotky (u vlastní nic nezmění).
            g.status = "done";
            if (up.value.reused) existingFound(g);
          },
          () => {
            pool.uploadSlots -= 1;
          },
        );
      },
      () => {
        if (!uploading) pool.uploadSlots -= 1;
      },
    );
  }

  /**
   * Nahrání s automatickým opakováním přechodných chyb (`UPLOAD_RETRY_DELAYS_MS`). Po prodlevě
   * se pokus nepošle, když dávka skončila resetem nebo skupina z dávky zmizela: vrátí se
   * poslední chyba a `track` ji pro cizí dávku zahodí.
   */
  async function sendUpload(owner: BatchState, g: Group, form: FormData) {
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await deps.upload(form);
      } catch (err) {
        const delay = UPLOAD_RETRY_DELAYS_MS[attempt];
        if (delay === undefined || !isTransientUploadFailure(err)) throw err;
        await deps.wait(delay);
        if (owner !== batch || !alive(g) || g.status !== "uploading") throw err;
      }
    }
  }

  /**
   * Nahrání a po chybě s neznámým výsledkem (`isUnknownUploadOutcome`, až po vyčerpání
   * opakování) ověření otisku přes `checkHashes` po prodlevách `UPLOAD_VERIFY_DELAYS_MS`: živá
   * fotka s otiskem souboru je ta, kterou server přijal, a vrátí se jako `reused` (hotovo +
   * převzetí, ne „Už v knihovně“; proto ne `applyMatch`). Bez živé shody, se shodou jen v koši
   * nebo při chybě ověření ani po poslední prodlevě zůstane původní chyba nahrání, takže
   * „Zkusit znovu“ soubor pošle znovu a server dedup nad otiskem drží jednu fotku. Po prodlevě
   * se ověření nepošle, když dávka skončila resetem nebo skupina z dávky zmizela.
   */
  async function uploadOrFind(
    owner: BatchState,
    g: Group,
    form: FormData,
  ): Promise<MediaUploadResponse> {
    try {
      return await sendUpload(owner, g, form);
    } catch (err) {
      if (!isUnknownUploadOutcome(err)) throw err;
      for (const delay of UPLOAD_VERIFY_DELAYS_MS) {
        if (delay > 0) await deps.wait(delay);
        if (owner !== batch || !alive(g) || g.status !== "uploading") throw err;
        let live: MediaHashMatch["live"] = null;
        try {
          live = (await deps.checkHashes([g.hash])).find((m) => m.hash === g.hash)?.live ?? null;
        } catch {
          // Ověření selhalo: výsledek nahrání zůstává neznámý, rozhodne další ověření nebo chyba.
        }
        if (live !== null) return { ...live, reused: true };
      }
      throw err;
    }
  }

  /** 4. Převzetí země a zdroje existujícími fotkami: jen `running`, jeden požadavek naráz. */
  function pumpAdopt() {
    const b = batch;
    if (b.session !== "running" || pool.adoptBusy || b.adoptQueue.length === 0) return;
    const opts = adoptOpts(b);
    if (opts === null) {
      b.adoptQueue = [];
      return;
    }
    const ids = [...new Set(b.adoptQueue)].slice(0, ADOPT_IDS_PER_REQUEST);
    const sent = new Set(ids);
    b.adoptQueue = b.adoptQueue.filter((id) => !sent.has(id));
    pool.adoptBusy = true;
    b.adoptSending = true;
    const body: AdoptBody = {
      action: "adoptFromBatch",
      ids,
      countryIds: [...opts.countryIds],
      source: opts.source,
    };
    track(
      () => deps.adopt(body),
      (res) => {
        if (res.ok) return;
        const failure = retryableFailure(res.error);
        for (const g of b.groups.values()) {
          if (g.status === "duplicate-live" && g.mediaId !== undefined && sent.has(g.mediaId)) {
            fail(g, "adopt", failure);
          }
        }
      },
      () => {
        pool.adoptBusy = false;
        b.adoptSending = false;
      },
    );
  }

  /** Opakování skupiny podle fáze chyby; `true` = něco se změnilo. */
  function retryGroup(g: Group): boolean {
    if (g.status !== "error") return false;
    const phase = g.phase;
    delete g.failure;
    delete g.phase;
    if (phase === "check") {
      g.status = "checking";
      g.checkSent = false;
    } else if (phase === "adopt") {
      g.status = "duplicate-live";
      if (g.mediaId !== undefined && adoptOpts(batch) !== null) batch.adoptQueue.push(g.mediaId);
    } else {
      g.status = "queued";
    }
    reopen();
    return true;
  }

  /** Opakování otisku souboru, který ho nedostal; `true` = něco se změnilo. */
  function retryRec(rec: Rec): boolean {
    if (rec.hash !== undefined || rec.status !== "error") return false;
    rec.status = "hashing";
    delete rec.failure;
    reopen();
    return true;
  }

  function retryKey(key: string): boolean {
    const rec = batch.recs.get(key);
    if (!rec) return false;
    const g = rec.hash === undefined ? undefined : batch.groups.get(rec.hash);
    return g ? retryGroup(g) : retryRec(rec);
  }

  function resetBatch() {
    batch.abort.abort();
    batch = createBatchState();
  }

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      pool.waiters.add(schedule);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) pool.waiters.delete(schedule);
      };
    },

    add(files) {
      if (files.length === 0) return;
      if (batch.session === "finished") resetBatch();
      const b = batch;
      const accepted = acceptBatchFiles(files);
      b.rejected += accepted.rejected;
      for (const file of accepted.accepted) {
        nextKey += 1;
        const key = `batch-${nextKey}`;
        b.order.push(key);
        b.recs.set(key, { key, file, status: "hashing", prechecked: false });
        // Fronta otisků vezme soubor až po vyřešení `precheck`: TIF bez dekodéru tak neodejde
        // ani v `check-hashes` (AK23), ani když `precheck` odpoví asynchronně.
        track(
          () => deps.precheck(file),
          (res) => {
            const r = b.recs.get(key);
            if (!r || r.status !== "hashing" || r.prechecked) return;
            if (res.ok && res.value) {
              r.status = "error-permanent";
              r.failure = { permanentReason: res.value };
            } else {
              // Bez rozhodnutí (např. selhala detekce schopností): trvalou chybu najde `prepare`.
              r.prechecked = true;
            }
          },
        );
      }
      settle();
    },

    remove(key) {
      const b = batch;
      const rec = b.recs.get(key);
      if (!rec) return;
      const g = rec.hash === undefined ? undefined : b.groups.get(rec.hash);
      if (g && g.members[0] === key && (g.status === "preparing" || g.status === "uploading")) {
        return;
      }
      if (g) {
        g.members = g.members.filter((k) => k !== key);
        if (g.members.length === 0) b.groups.delete(g.hash);
      }
      b.recs.delete(key);
      b.views.delete(key);
      b.order = b.order.filter((k) => k !== key);
      settle();
    },

    start(batchOpts) {
      const b = batch;
      if (b.session === "running") return;
      b.opts = batchOpts;
      b.session = "running";
      b.startedAt = deps.now();
      b.finishedAt = null;
      b.adopted = new Set();
      b.adoptQueue = [];
      for (const g of b.groups.values()) if (g.status === "duplicate-live") existingFound(g);
      settle();
    },

    retry(key) {
      if (retryKey(key)) settle();
    },

    retryAllFailed() {
      let changed = false;
      for (const rec of batch.recs.values()) changed = retryRec(rec) || changed;
      for (const g of batch.groups.values()) changed = retryGroup(g) || changed;
      if (changed) settle();
    },

    restoreFromTrash(key) {
      const rec = batch.recs.get(key);
      const g = rec?.hash === undefined ? undefined : batch.groups.get(rec.hash);
      if (!g || g.status !== "duplicate-trash" || g.mediaId === undefined) {
        return Promise.resolve();
      }
      const mediaId = g.mediaId;
      /** Chyba obnovy pro volajícího (i po `reset`); 409 živého dvojčete chybou volajícího není. */
      let failed: { error: unknown } | null = null;
      return track(
        async () => {
          try {
            await deps.restore(mediaId);
            return "restored" as const;
          } catch (error) {
            const { status, code } = readFailure(error);
            if (status === 409 && code === "media_restore_live_twin") return "live-twin" as const;
            failed = { error };
            throw error;
          }
        },
        (out) => {
          if (!out.ok || !alive(g) || g.status !== "duplicate-trash") return;
          if (out.value === "restored") {
            g.status = "duplicate-live";
            existingFound(g);
          } else {
            // Mezitím vzniklo živé dvojče; jeho ID 409 adminu nedá. Otisk se vrátí do fronty
            // kontroly a přechod na shodu udělá `applyMatch` jako u každého jiného otisku.
            g.status = "checking";
            g.checkSent = false;
            delete g.mediaId;
          }
          reopen();
        },
      ).then(() => {
        if (failed) throw failed.error;
      });
    },

    reset() {
      resetBatch();
      settle();
    },
  };
}
